import React from 'react'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { MkAccountProvider } from './core'
import { WishlistProvider } from '@contexts/WishlistContext'

/**
 * Session-aware account provider that remounts child providers when user changes
 * This ensures complete state reset during logout/login transitions
 *
 * When a user logs out and another user logs in, this component detects the session
 * change via the providerKey and forces a remount of MkAccountProvider and WishlistProvider.
 * This ensures all account-related state is cleared and initialized fresh for the new user.
 *
 * AnalyticsProvider lives just below this in `AppProviders` (it consumes
 * `useAccountContext()` from `MkAccountProvider`); it remounts with `MkAccountProvider`
 * on `providerKey` change since it sits inside this subtree.
 */
export const SessionAwareAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isAuthorized } = useAuthentication()

  // Create a key that changes when the user changes
  // Use session username or a combination of isAuthorized + timestamp to force remount
  const providerKey = React.useMemo(() => {
    if (!session || !isAuthorized) {
      // When logged out, use a stable key so providers stay mounted but empty
      return 'logged-out'
    }
    // When logged in, use username to ensure providers remount for different users
    return session.authenticatedUserName || session.username || `auth-${isAuthorized}-${Date.now()}`
  }, [session, isAuthorized])

  return (
    <MkAccountProvider key={providerKey}>
      <WishlistProvider key={providerKey}>{children}</WishlistProvider>
    </MkAccountProvider>
  )
}
